export const statusFilterOrder = ["all", "active", "deactivated"] as const;
export type StatusFilter = (typeof statusFilterOrder)[number];

export function isStatusFilter(value: string): value is StatusFilter {
  return (statusFilterOrder as readonly string[]).includes(value);
}

export type SortColumn = "name" | "joined" | "lastActive" | "groups";

type FilterableMember = {
  name: string;
  email: string;
  deactivated: boolean | null;
};

export function filterMembers<T extends FilterableMember>(
  members: readonly T[],
  search: string,
  statusFilter: StatusFilter,
  matchesGroup: (member: T) => boolean
): T[] {
  const lowerSearch = search.toLowerCase();
  return members.filter((m) => {
    const isDeactivated = m.deactivated === true;
    if (statusFilter === "active" && isDeactivated) return false;
    if (statusFilter === "deactivated" && !isDeactivated) return false;
    if (!matchesGroup(m)) return false;
    if (!lowerSearch) return true;
    return (
      m.name.toLowerCase().includes(lowerSearch) ||
      m.email.toLowerCase().includes(lowerSearch)
    );
  });
}

type SortableMember = FilterableMember & {
  id: string;
  createdAt: number | null;
  lastActiveAt: number | null;
};

export function sortMembers<T extends SortableMember>(
  members: readonly T[],
  sortColumn: SortColumn | null,
  sortDirection: "asc" | "desc",
  groupNamesForMember: (id: string) => string[]
): T[] {
  const list = [...members];
  list.sort((a, b) => {
    if (!sortColumn) return (a.createdAt ?? 0) - (b.createdAt ?? 0);

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "joined":
        aVal = a.createdAt ?? 0;
        bVal = b.createdAt ?? 0;
        break;
      case "lastActive":
        aVal = a.lastActiveAt ?? 0;
        bVal = b.lastActiveAt ?? 0;
        break;
      case "groups":
        aVal = groupNamesForMember(a.id).join(", ").toLowerCase();
        bVal = groupNamesForMember(b.id).join(", ").toLowerCase();
        break;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
  return list;
}
